import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEYS, readStored, writeStored } from '@/lib/storage';

export type Theme = 'light' | 'dark';

/** Browser UI tint per theme, mirrored into the `theme-color` meta tag. */
const THEME_COLOR: Record<Theme, string> = {
  light: '#04593B',
  dark: '#0A1512',
};

function parseTheme(value: unknown): Theme | null {
  return value === 'light' || value === 'dark' ? value : null;
}

/**
 * Light is the default.
 *
 * The site does not follow `prefers-color-scheme`: its light presentation is
 * the designed one, and a visitor whose OS happens to be dark should still
 * arrive at it. Dark mode stays one deliberate click away, and that choice is
 * remembered.
 */
function initialTheme(): Theme {
  return readStored(STORAGE_KEYS.theme, parseTheme) ?? 'light';
}

export function useTheme(): {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
} {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    // Reflect the theme on <html> so CSS variables and `color-scheme` apply.
    document.documentElement.setAttribute('data-theme', theme);

    // Keep the browser UI tint in step. The meta tag cannot be keyed to the
    // theme with a media query, because the theme is ours, not the system's.
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark';
      writeStored(STORAGE_KEYS.theme, next);
      return next;
    });
  }, []);

  return { theme, isDark: theme === 'dark', toggleTheme };
}
